import styled from "@emotion/styled";
import _, { cloneDeep, isEmpty } from "lodash";
import { FormEvent, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { useHistory, useParams } from "react-router-dom";
import { create } from "zustand";

import CreateCombinedSensorsChart from "components/dashboard/create/CreateCombinedSensorsChart";
import UpdateHeatmapPanel from "components/dashboard/update/UpdateHeatmapPanel";
import UpdateCombinedGatewayPanel from "components/dashboard/update/UpdateCombinedGatewayPanel";
import {
    FETCH_CHART_FAIL_MESSAGE,
    HttpStatus,
    INVALID_OPERATION_MESSAGE,
} from "constant";
import { ChartSource, ChartType } from "constant/chart";
import { PanelV2 } from "generated/models";
import { useMetricDefined } from "hooks/useMetricDefined";
import usePanelConfiguration from "hooks/usePanelConfiguration";
import dashboardService from "service/dashboardService";
import { getAPIError, showErrorAlert } from "utils/alert";
import { isHttpSuccess } from "utils/functions";
import { fetchDevices } from "utils/gatewayFunctions";
import { isPSLWidget, validateChartColors } from "utils/chart";
import { DeviceMapRequest } from ".";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    ContainerStyled,
    useCreateDashboardStore,
} from "../create";
import { MultipleChartBlock } from "../create/MultipleChartBlock";
import { MultipleDeviceChartBlock } from "../create/MultipleDeviceChartBlock";
import { CorrelationChartBlock } from "../create/ActuatorWidget/CorrelationChartBlock";
import CombinedConsumtionChartUpdate from "./CombinedConsumptionChartUpdate";
import {
    UpdateWidgetTemplates,
    WidgetUpdateTemplateType,
} from "./UpdateTemplates";
import UpdatePSLWidget from "./UpdatePSLWidget";
import { UpdateCardMultiple } from "./UpdateCardMultiple";
import { UpdateLDSUCard } from "./UpdateLDSUCard";
import {
    getWidgetConfigByWidgetType,
    getWidgetTypeFromChartSource,
} from "../utils/widgetConfig";
import { isUpdateSubmitInvalid } from "../utils/submitValidation";

const ContentWrapper = styled.div`
    .table-line {
        margin-top: -0.75rem;
        margin-bottom: -0.75rem;
    }

    .update-row {
        padding-top: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid #3e4b67;
    }

    .single-update-form-control.form-control {
        width: unset;
    }

    .mute {
        color: #828fab;
    }

    .single-edit-switch .custom-switch {
        right: 1rem;
    }
`;

const ChartUpdateWrapper = styled.div`
    background-color: #2f3a51;
    padding: 20px;
    border-radius: 10px;
    margin-bottom: 20px;
`;

const WidgetSourceMap: Record<string, WidgetUpdateTemplateType> = {
    SINGLE_LINE: WidgetUpdateTemplateType.SENSOR,
    STORAGE: WidgetUpdateTemplateType.STORAGE,
    NOTIFIER: WidgetUpdateTemplateType.NOTIFIER,
    CARD_NUMBER_ONLY: WidgetUpdateTemplateType.SENSOR_CARD,
    CARD_BAR: WidgetUpdateTemplateType.SENSOR_CARD,
    CARD_TEXT: WidgetUpdateTemplateType.SENSOR_CARD,
    GAUGE: WidgetUpdateTemplateType.GAUGE,
    GAUGE_COLOURS: WidgetUpdateTemplateType.GAUGE_COLORS,
    CARD_GATEWAY: WidgetUpdateTemplateType.CARD_GATEWAY,
    DONUT: WidgetUpdateTemplateType.GATEWAY,
};

/** Dashboard panel in single-chart edit flow; fields depend on chart source (API-shaped object map). */
interface Panel {
    [key: string]: any;
}

type useStoreProps = {
    deviceList: any[];
    setDeviceList: (deviceList: any[]) => void;
    allRegistry: any[];
    setAllRegistry: (allRegistry: any[]) => void;
    dashboardId: string;
    setDashboardId: (dashboardId: string) => void;
    panel: Panel;
    originPanel: Panel;
    setPanel: (panel: Panel) => void;
    setOriginPanel: (panel: Panel) => void;
    updatePanel: (key: string, value: any) => void;
    getDeviceInfo: (data: any) => any;
    isSaving: boolean;
    setIsSaving: (isSaving: boolean) => void;
};

export const useStore = create<useStoreProps>((set, get) => ({
    deviceList: [],
    setDeviceList: (deviceList) => {
        set({ deviceList });
        const store = get();
        store.getDeviceInfo(store.panel);
    },
    allRegistry: [],
    setAllRegistry: (allRegistry) => set({ allRegistry }),
    dashboardId: "",
    setDashboardId: (dashboardId) => set({ dashboardId }),
    panel: {} as Panel,
    originPanel: {} as Panel,
    setPanel: (panel) => set({ panel }),
    setOriginPanel: (panel) => set({ originPanel: panel }),
    updatePanel: (key, value) => {
        const current = _.get(get().panel, String(key));
        if (
            (current === undefined || current === value) &&
            key !== "image"
        ) {
            return;
        }

        set((state) => _.set(state.panel, String(key), value));
    },
    getDeviceInfo: (data) => {
        if (!isEmpty(data)) {
            let deviceInfo = {};

            if (data.attributes?.length === 0) {
                return deviceInfo;
            }

            // If the gateway_id is available but the said is not, it means that the current chart is using gateway level data.
            if (
                data.attributes[0].gateway_id &&
                !data.attributes[0].hasOwnProperty("said") &&
                !data.attributes[0].device_id
            ) {
                deviceInfo = get().allRegistry?.find(
                    (registry) =>
                        registry.gateway_id === data.attributes[0].gateway_id
                );
            } else {
                deviceInfo = get().deviceList?.find(
                    (device) =>
                        device.ldsuUID === data.attributes[0].device_id &&
                        Number(device.said) === Number(data.attributes[0].said)
                );
            }

            return deviceInfo ?? {};
        }
    },
    isSaving: false,
    setIsSaving: (isSaving) => set({ isSaving }),
}));

const MultipleChartUpdate = ({
    panel,
    onSubmitMultipleChart,
}: {
    panel: Panel;
    onSubmitMultipleChart: Function;
}) => {
    const { deviceList, updatePanel } = useStore();

    const { defaultValue } = usePanelConfiguration({
        panelData: panel,
        deviceList,
    });

    const isActuatorChart = panel?.source === ChartSource.MULTIPLE_ACTUATORS;

    const handleChange = (updateData: any) => {
        if (!_.isEmpty(updateData.data)) {
            updatePanel("name", updateData.chart_name);

            const { multipleChartType } = updateData;

            const updateDataDevices = {
                ..._.get(updateData, "data[0].devices"),
            };

            if (multipleChartType === "different") {
                Object.assign(updateDataDevices, {
                    ..._.get(updateData, "data[1].devices"),
                });
            }

            updatePanel("attributes", Object.values(updateDataDevices));
        }
    };

    if (isActuatorChart) {
        return (
            <MultipleDeviceChartBlock
                deviceType="Actuator"
                blockKey={{ key: _.get(panel, "uuid", "actuator") }}
                defaultValue={defaultValue}
                isSingleUpdate
                isEdit
                onChange={handleChange}
                onSubmitMultipleChart={onSubmitMultipleChart}
            />
        );
    }

    return (
        <MultipleChartBlock
            blockKey={{ key: _.get(panel, "uuid", "sensor") }}
            defaultValue={defaultValue}
            isSingleUpdate
            isEdit
            onChange={handleChange}
            onSubmitMultipleChart={onSubmitMultipleChart}
        />
    );
};

const CorrelationChartUpdate = ({
    panel,
    onSubmitMultipleChart,
}: {
    panel: any;
    onSubmitMultipleChart: Function;
}) => {
    const { deviceList, updatePanel } = useStore();

    const { defaultValue } = usePanelConfiguration({
        panelData: panel,
        deviceList,
    });

    const handleSave = (updateData: any) => {
        onSubmitMultipleChart(updateData);
    };

    // Only pass defaultValue when deviceList is ready; otherwise the hook would
    // put all attributes in the sensor column (no isActuator when device is missing).
    const hasAttributes = (panel?.attributes?.length ?? 0) > 0;
    const canReliablySplit = !hasAttributes || (deviceList?.length ?? 0) > 0;
    const safeDefaultValue = canReliablySplit
        ? defaultValue
        : {
            ...defaultValue,
            data: {},
        };

    return (
        <CorrelationChartBlock
            blockKey={{ key: _.get(panel, "uuid", "correlation") }}
            defaultValue={safeDefaultValue}
            isSingleUpdate
            isEdit
            onChange={(updateData) => {
                if (!_.isEmpty(updateData.data)) {
                    updatePanel("name", updateData.chart_name);

                    const sensorDevices =
                        _.get(updateData, "data[0].devices") || {};
                    const actuatorDevices =
                        _.get(updateData, "data[1].devices") || {};

                    const updateDataDevices = {
                        ...sensorDevices,
                        ...actuatorDevices,
                    };

                    const attributes = Object.values(updateDataDevices);

                    updatePanel("attributes", attributes);
                }
            }}
            onSubmitMultipleChart={handleSave}
        />
    );
};

const getTemplateType = (
    source?: ChartSource,
    chart?: ChartType
): WidgetUpdateTemplateType => {
    if (
        source === ChartSource.SINGLE_GATEWAY &&
        chart === ChartType.CARD_GATEWAY
    ) {
        return WidgetUpdateTemplateType.CARD_GATEWAY;
    }

    if (source === ChartSource.SINGLE_SENSOR && chart === ChartType.BAR) {
        return WidgetUpdateTemplateType.SINGLE_CONSUMPTION;
    }

    // PSL Widget (Room/Zone/Group determined by attributes[0].name)
    // PSL Room Usage Widget
    if (
        (source === ChartSource.MULTIPLE_SENSORS && chart === ChartType.BAR) ||
        // PSL Room Status Widget
        (source === ChartSource.SINGLE_SENSOR &&
            chart === ChartType.MULTIPLE_LINES) ||
        // PSL Group/Zone Status Widget
        (source === ChartSource.SINGLE_SENSOR &&
            chart === ChartType.SINGLE_STEP_LINE)
    ) {
        return WidgetUpdateTemplateType.PSL;
    }

    if (
        chart === ChartType.SINGLE_LINE &&
        source === ChartSource.SINGLE_ACTUATOR
    ) {
        return WidgetUpdateTemplateType.ACTUATOR;
    }

    let templateType: string = chart ?? "";
    if (chart === ChartType.HEATMAP && source === ChartSource.SINGLE_SENSOR) {
        return WidgetUpdateTemplateType.INDIVIDUAL_HEATMAP;
    }

    if (source && [ChartSource.EVENT, ChartSource.NOTIFIER].includes(source)) {
        return WidgetUpdateTemplateType.EVENT;
    }

    if (source === ChartSource.SUBSCRIPTION) {
        return WidgetUpdateTemplateType.SUBSCRIPTION;
    }

    return WidgetSourceMap[templateType] ?? WidgetUpdateTemplateType.SENSOR;
};

const UpdateChartPanel = (props: any) => {
    const store = useStore();
    const { panel, isSaving } = store;
    const panelSource = panel?.source;

    let widgetChartType = getTemplateType(panelSource, panel?.chart);

    const isMultiSensorLineChart =
        panel?.chart === ChartType.CARD_MULTIPLE_LINES_TYPE2 &&
        panel?.source === ChartSource.MULTIPLE_SENSORS;

    widgetChartType = isMultiSensorLineChart
        ? WidgetUpdateTemplateType.SENSOR_CARD
        : widgetChartType;

    const template = cloneDeep(UpdateWidgetTemplates[widgetChartType]);

    const isCorrelationChartUpdate =
        panelSource === ChartSource.SENSORS_AND_ACTUATORS &&
        panel?.chart === ChartType.MULTIPLE_LINES;

    const isMultipleChartUpdate =
        (panelSource === ChartSource.MULTIPLE_SENSORS ||
            panelSource === ChartSource.MULTIPLE_ACTUATORS) &&
        panel.chart === ChartType.MULTIPLE_LINES;

    const renderUpdateContent = () => {
        if (isCorrelationChartUpdate) {
            return (
                <CorrelationChartUpdate
                    panel={panel}
                    onSubmitMultipleChart={props.onSubmitMultipleChart}
                />
            );
        }

        if (isMultipleChartUpdate) {
            return (
                <MultipleChartUpdate
                    panel={panel}
                    onSubmitMultipleChart={props.onSubmitMultipleChart}
                />
            );
        }

        return template?.map((item) => {
            const value = item.render
                ? item.render(panel, store)
                : _.get(panel, item.key, "-");

            if (value === null) return null;

            // Support dynamic labels (function) or static labels (string)
            const label =
                typeof item.label === "function"
                    ? item.label(panel)
                    : item.label;

            return (
                <Row
                    key={item.key}
                    className="update-row justify-content-between align-items-center"
                >
                    <Col>{label}</Col>
                    <Col className="d-flex justify-content-end">{value}</Col>
                </Row>
            );
        });
    };

    return (
        <fieldset disabled={isSaving}>
            <Row>
                <div className="w-100 mb-5">
                    <Col sm={12} className="form-box">
                        <Row className="cstm-table">
                            <Col sm={12}>
                                <div className="table-line border-0 py-0">
                                    {renderUpdateContent()}
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </div>
            </Row>
        </fieldset>
    );
};

const isDisabledSubmit = ({
    isSaving,
    panel,
}: {
    isSaving: boolean;
    panel: Panel;
}): boolean => {
    if (isSaving) {
        return true;
    }

    const widgetType = getWidgetTypeFromChartSource(
        panel?.chart,
        panel?.source
    );
    const widgetConfig =
        typeof widgetType === "number"
            ? getWidgetConfigByWidgetType(widgetType)
            : undefined;

    if (typeof widgetType !== "number" || !widgetConfig) {
        return true;
    }

    return isUpdateSubmitInvalid({
        panel,
        widgetType,
        widgetConfig,
    });
};

const UpdatePanel = () => {
    const { dashboardId, panelId } = useParams<any>();
    const [loading, setLoading] = useState(true);
    const {
        panel,
        setPanel,
        setOriginPanel,
        setDeviceList,
        setAllRegistry,
        isSaving,
        setIsSaving,
        updatePanel,
        deviceList,
    } = useStore();

    const history = useHistory();
    useMetricDefined();
    useEffect(() => {
        fetchDevices().then((res: any) => {
            setDeviceList(res.deviceList || []);
            setAllRegistry(res.allRegistry || []);
            useCreateDashboardStore.setState({
                deviceList: res.deviceList || [],
                allRegistry: res.allRegistry || [],
            });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLoading(true);
        setPanel({} as Panel);
        dashboardService
            .getSinglePanel(dashboardId, panelId)
            .then((res: any) => {
                if (!isHttpSuccess(res?.status)) {
                    showErrorAlert(getAPIError(res, FETCH_CHART_FAIL_MESSAGE));
                    return;
                }

                const data = _.get(res, "data");

                // Normalize the data to ensure all necessary fields are present for the update form
                if (data.chart === ChartType.CARD_GATEWAY) {
                    const attributes = _.get(data, "attributes", []);
                    if (attributes[0]?.show_location === undefined) {
                        attributes[0] = {
                            ...attributes[0],
                            show_location: false, // Default value
                        };
                    }
                }

                setPanel({
                    ...data,
                    updateData: {},
                });
                setOriginPanel(data);
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId, panelId]);

    const UpdateContainer =
        (panel?.source === ChartSource.MULTIPLE_SENSORS ||
            panel?.source === ChartSource.MULTIPLE_ACTUATORS) &&
            ChartType.MULTIPLE_LINES === panel?.chart
            ? ContainerStyled
            : Container;

    const handleUpdate = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!panel?.uuid) {
            return;
        }

        setIsSaving(true);

        const chartSource: ChartSource = panel?.source;
        const widgetType = getWidgetTypeFromChartSource(
            panel?.chart,
            panel?.source
        );
        const template =
            widgetType !== null && DeviceMapRequest[widgetType]
                ? DeviceMapRequest[widgetType]
                : DeviceMapRequest[chartSource];

        const paramsTemplate = cloneDeep(template);
        const params = objectFluent(paramsTemplate, panel);

        // Currently updateData just for image upload/delete.
        // Because checking the panel.image, we would not know if an image is updated.
        const updateData = panel?.updateData || {};
        if (!CHART_NAME_PATTERN.test(panel.name.trim())) {
            setIsSaving(false);
            showErrorAlert({ message: CHART_ERROR_MESSAGE });
            return;
        }

        const chartColorValidates = validateChartColors({
            attributes: params.attributes,
            chart: params.chart,
            source: params.source,
        });

        if (chartColorValidates.length) {
            setIsSaving(false);
            const message = chartColorValidates[0].message;
            showErrorAlert({ message });
            return;
        }

        if (isCombinedSensorChart || isCombinedConsumptionChart) {
            const sensorCount = params.attributes.length;
            const MAX_SENSOR_COUNT = 2;
            if (sensorCount < MAX_SENSOR_COUNT) {
                setIsSaving(false);
                showErrorAlert({
                    message: `Please select at least ${MAX_SENSOR_COUNT} sensors to create a combined chart.`,
                });
                return;
            }
        }

        try {
            const updatePanelResponse = await dashboardService.updatePanel(
                dashboardId,
                panel.uuid,
                {
                    ...params,
                    name: _.trim(params.name.replaceAll(/\s+/g, " ")),
                }
            );

            if (updateData.image) {
                const uploadRes = await dashboardService.uploadPanelImage(
                    dashboardId,
                    panel.uuid,
                    panel.image
                );

                if (!isHttpSuccess(uploadRes.status)) {
                    showErrorAlert(
                        getAPIError(uploadRes, "Upload image failed.")
                    );
                }
            }

            if (updateData.image === null) {
                await dashboardService.deletePanelImage(
                    dashboardId,
                    panel.uuid
                );
            }

            if (!isHttpSuccess(updatePanelResponse.status)) {
                showErrorAlert(
                    getAPIError(updatePanelResponse, INVALID_OPERATION_MESSAGE)
                );
            }

            if (updatePanelResponse.status === HttpStatus.NO_CONTENT) {
                history.push("/dashboard");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isNetwork = isPSLWidget(panel?.source, panel?.chart);
    const isCombinedGatewayChart =
        panel?.source === ChartSource.MULTIPLE_GATEWAYS;

    const isCombinedHeatmap =
        panel?.chart === ChartType.HEATMAP && panel?.attributes?.length > 1;

    const isCombinedSensorChart =
        panel?.source === ChartSource.MULTIPLE_SENSORS &&
        panel?.chart === ChartType.SINGLE_LINE;

    const isCombinedConsumptionChart =
        panel?.source === ChartSource.MULTIPLE_SENSORS &&
        panel?.chart === ChartType.STACKED_BAR;

    const isMultipleCardChart =
        panel?.chart === ChartType.CARD_MULTIPLE_LINES &&
        panel?.source === ChartSource.MULTIPLE_SENSORS;

    const isLDSUCard =
        panel?.chart === ChartType.CARD_MULTIPLE_LINES_TYPE3 ||
        panel?.chart === ChartType.CARD_MULTIPLE_LINES_TYPE2;

    /** @deprecated Use the WidgetConfig instead */
    const isDefaultUpdateFlow =
        !isCombinedHeatmap &&
        !isCombinedSensorChart &&
        !isCombinedConsumptionChart &&
        !isNetwork &&
        !isCombinedGatewayChart &&
        !isMultipleCardChart &&
        !isLDSUCard;

    const disabled = isDisabledSubmit({
        isSaving,
        panel,
    });

    if (loading) {
        return (
            <ContentWrapper className="page-content update-single-screen">
                <Spinner animation="border" variant="primary" />
            </ContentWrapper>
        );
    }

    return (
        <ContentWrapper className="page-content update-single-screen">
            <UpdateContainer>
                <Form onSubmit={handleUpdate}>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">
                                Edit Dashboard Widget
                            </h5>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12">
                            {isCombinedGatewayChart ? (
                                <ChartUpdateWrapper>
                                    <UpdateCombinedGatewayPanel
                                        panel={panel as PanelV2}
                                        onChange={(
                                            field: string,
                                            value: any
                                        ) => {
                                            updatePanel(field, value);
                                        }}
                                    />
                                </ChartUpdateWrapper>
                            ) : (
                                ""
                            )}

                            {isCombinedHeatmap ? (
                                <ChartUpdateWrapper>
                                    <UpdateHeatmapPanel
                                        panel={panel}
                                        deviceList={deviceList}
                                        onChange={(
                                            field: string,
                                            value: any
                                        ) => {
                                            if (
                                                field === "image" ||
                                                field === "image_uuid"
                                            ) {
                                                // Use another variable to check if the image is updated/deleted.
                                                updatePanel("updateData", {
                                                    ...panel?.updateData,
                                                    [field]: value,
                                                });
                                            }
                                            updatePanel(field, value);
                                        }}
                                    />
                                </ChartUpdateWrapper>
                            ) : (
                                ""
                            )}

                            {isCombinedSensorChart ? (
                                <ChartUpdateWrapper>
                                    <CreateCombinedSensorsChart
                                        isUpdate
                                        panel={panel}
                                        gatewayIds={(
                                            panel?.attributes || []
                                        ).map((attr: any) => attr.gateway_id)}
                                        onChange={(data: any) => {
                                            updatePanel("name", data.chartName);
                                            if (data.sensorList) {
                                                updatePanel(
                                                    "attributes",
                                                    data.sensorList.map(
                                                        (sensor: any) => ({
                                                            ...sensor,
                                                            color: data.chartColor,
                                                        })
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                </ChartUpdateWrapper>
                            ) : (
                                ""
                            )}

                            {isCombinedConsumptionChart ? (
                                <CombinedConsumtionChartUpdate
                                    panel={panel}
                                    devices={deviceList}
                                    onChange={(data: any) => {
                                        updatePanel("name", data.chartName);
                                        if (data.data) {
                                            updatePanel(
                                                "attributes",
                                                data.data
                                            );
                                        }
                                    }}
                                />
                            ) : (
                                ""
                            )}

                            {isNetwork && (
                                <UpdatePSLWidget
                                    panel={panel}
                                    onChange={(data: any) => {
                                        updatePanel("name", data.chartName);
                                        if (data.data) {
                                            updatePanel(
                                                "attributes",
                                                data.data
                                            );
                                        }
                                    }}
                                />
                            )}

                            {isMultipleCardChart ? (
                                <UpdateCardMultiple
                                    panel={panel}
                                    devices={deviceList}
                                    onChange={(data: any) => {
                                        updatePanel("name", data.chartName);
                                        if (data.data) {
                                            updatePanel(
                                                "attributes",
                                                data.data
                                            );
                                        }
                                    }}
                                />
                            ) : (
                                ""
                            )}

                            {isLDSUCard ? (
                                <UpdateLDSUCard
                                    panel={panel}
                                    devices={deviceList}
                                    onChange={(field: string, value: any) => {
                                        updatePanel(field, value);
                                    }}
                                />
                            ) : (
                                ""
                            )}

                            {isDefaultUpdateFlow ? (
                                <UpdateChartPanel
                                    onSubmitMultipleChart={handleUpdate}
                                />
                            ) : (
                                ""
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" className="d-flex justify-content-center">
                            <Button
                                className="text-uppercase"
                                variant="secondary"
                                onClick={() => {
                                    history.push("/dashboard");
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="text-uppercase ml-2"
                                variant="primary"
                                type="submit"
                                disabled={disabled}
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </UpdateContainer>
        </ContentWrapper>
    );
};

export default UpdatePanel;
export { UpdateChartPanel };
export type { useStoreProps };
